import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  MessageCircle,
  Clock,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { quotesAPI } from '../services/api';
import ApplianceTable from './ApplianceTable';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
    appliancesData: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleAppliancesChange = useCallback((data) => {
    handleInputChange('appliancesData', data);
  }, [handleInputChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.projectType || !formData.message.trim()) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    // Validation du simulateur de puissance (obligatoire)
    console.log('FormData appliancesData:', formData.appliancesData);
    
    if (!formData.appliancesData || !formData.appliancesData.appliances || formData.appliancesData.appliances.length === 0) {
      toast.error("Veuillez compléter le simulateur de puissance", {
        description: "Le tableau de consommation est obligatoire pour dimensionner votre installation.",
        duration: 5000,
      });
      return;
    }

    // Vérifier qu'il y a au moins un appareil avec une puissance > 0
    console.log('Appliances:', formData.appliancesData.appliances);
    const hasValidAppliance = formData.appliancesData.appliances.some(
      app => app.name && app.name.trim() && app.quantity > 0 && (app.unitPower > 0 || app.power > 0)
    );
    console.log('Has valid appliance:', hasValidAppliance);
    
    if (!hasValidAppliance) {
      toast.error("Veuillez ajouter au moins un appareil dans le simulateur", {
        description: "Ajoutez au moins un appareil avec sa puissance et son utilisation quotidienne.",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send quote request to API
      const response = await quotesAPI.create(formData);
      
      toast.success("Demande envoyée avec succès !", {
        description: `Nous vous recontacterons sous 24h pour étudier votre projet. Référence: ${response.id.substring(0, 8)}`,
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        message: '',
        appliancesData: null
      });
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Erreur lors de l'envoi", {
        description: "Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    "Installation domestique",
    "Installation résidentielle",
    "Projet commercial",
    "Collectivité publique",
    "Installation industrielle",
    "Remplacement de batteries",
    "Maintenance système existant",
    "Autre projet"
  ];

  const companyInfo = {
    addresses: [
      {
        type: "Siège social",
        address: "Moisenay, Région Parisienne, France",
        country: "France"
      },
      {
        type: "Bureau commercial",
        address: "Bamako, Mali",
        country: "Mali"
      }
    ],
    contact: {
      emails: ["contact@znsolar.fr", "seydoumalle@znsolar.fr"],
      phone: "06 23 73 77 80",
      whatsapp: "06 23 73 77 80"
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-yellow-100 text-yellow-800 px-4 py-2">
            Contact
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Parlons de Votre
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-gray-700">
              Projet Énergétique
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Obtenez un devis personnalisé et gratuit. Notre équipe d'experts vous accompagne 
            dans la réalisation de votre projet énergétique.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Demande de Devis Gratuit
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Remplissez ce formulaire et recevez votre devis personnalisé sous 24h
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Votre nom et prénom"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="votre.email@exemple.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+33 6 XX XX XX XX"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="projectType">Type de projet *</Label>
                      <Select 
                        value={formData.projectType} 
                        onValueChange={(value) => handleInputChange('projectType', value)}
                        disabled={isSubmitting}
                        required
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Sélectionnez votre projet" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Description du projet *</Label>
                    <Textarea
                      id="message"
                      placeholder="Décrivez votre projet : localisation, besoins énergétiques, contraintes spécifiques..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      required
                      className="min-h-[120px] resize-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Tableau des appareils */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1">
                        ⚡ Simulateur de Puissance
                        <span className="text-red-600">*</span>
                      </span>
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-semibold whitespace-nowrap">Obligatoire</span>
                    </Label>
                    <p className="text-sm text-gray-600 mb-3">
                      Complétez ce tableau pour nous aider à dimensionner votre installation solaire
                    </p>
                    <ApplianceTable 
                      onChange={handleAppliancesChange}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Envoi en cours...</span>
                      </div>
                    ) : (
                      <>
                        Envoyer ma demande
                        <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    * Champs obligatoires. Vos données sont protégées et ne seront jamais partagées.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Quick Contact */}
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">
                  Contact Direct
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Téléphone</p>
                    <p className="text-gray-600">{companyInfo.contact.phone}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">WhatsApp</p>
                    <p className="text-gray-600">{companyInfo.contact.whatsapp}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    {companyInfo.contact.emails.map((email, index) => (
                      <p key={index} className="text-gray-600 text-sm">{email}</p>
                    ))}
                  </div>
                </div>

                {/* WhatsApp Button */}
                <a
                  href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '33623737780'}?text=Bonjour, je souhaite obtenir un devis pour mes batteries lithium PowMr`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition-all duration-200 hover:scale-105 shadow-md flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Contacter via WhatsApp</span>
                </a>
              </CardContent>
            </Card>

            {/* Office Locations */}
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">
                  Nos Bureaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {companyInfo.addresses.map((address, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{address.type}</p>
                      <p className="text-gray-600 text-sm">{address.address}</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {address.country}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Response Time */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-yellow-50 to-gray-50">
              <CardContent className="p-6 text-center">
                <Clock className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Réponse Garantie</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Nous nous engageons à vous répondre sous 24h maximum
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Service client réactif</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};